// src/utils/permissions.js

// 1. ROLES
export const ROLES = {
    OWNER: 'Owner/System Admin',
    ADMIN: 'Admin',
    MANAGER: 'Manager',
    OFFICE: 'Office Staff',
    CONSULTANT: 'Sales Consultant',
    TECH: 'Field Technician'
};

// 2. PERMISSIONS
export const PERMISSIONS = {
    // --- HR ---
    VIEW_HR: 'VIEW_HR',
    MANAGE_EMPLOYEES: 'MANAGE_EMPLOYEES',
    VIEW_SENSITIVE_INFO: 'VIEW_SENSITIVE_INFO',
    MANAGE_HIRING: 'MANAGE_HIRING',

    // --- SALES ---
    VIEW_CUSTOMERS: 'VIEW_CUSTOMERS',
    MANAGE_CUSTOMERS: 'MANAGE_CUSTOMERS',
    MANAGE_LEADS: 'MANAGE_LEADS',

    // --- JOBS ---
    VIEW_JOBS: 'VIEW_JOBS',
    MANAGE_JOBS: 'MANAGE_JOBS',
    COMPLETE_JOBS: 'COMPLETE_JOBS',

    // --- FINANCIALS ---
    VIEW_FINANCIALS: 'VIEW_FINANCIALS',
    MANAGE_FINANCIALS: 'MANAGE_FINANCIALS',
    MANAGE_EXPENSES: 'MANAGE_EXPENSES',

    // --- ROUTES ---
    // MANAGE_ROUTES: Create/edit route groups, run optimization, save route orders
    // VIEW_ROUTES: View own optimized route for the day (field workers, techs)
    MANAGE_ROUTES: 'MANAGE_ROUTES',
    VIEW_ROUTES: 'VIEW_ROUTES',
    MANAGE_SCHEDULE: 'MANAGE_SCHEDULE',  // Run weekly territory planner, batch-create jobs

    // --- ADMIN ---
    MANAGE_SETTINGS: 'MANAGE_SETTINGS',
    APPROVE_TIMESHEETS: 'APPROVE_TIMESHEETS',
    VIEW_REPORTS: 'VIEW_REPORTS',
    MANAGE_MARKETING: 'MANAGE_MARKETING'
};

// 3. THE MATRIX
const ROLE_PERMISSIONS = {
    [ROLES.MANAGER]: [
        PERMISSIONS.VIEW_HR,
        PERMISSIONS.MANAGE_HIRING,
        PERMISSIONS.VIEW_CUSTOMERS,
        PERMISSIONS.MANAGE_CUSTOMERS,
        PERMISSIONS.MANAGE_LEADS,
        PERMISSIONS.VIEW_JOBS,
        PERMISSIONS.MANAGE_JOBS,
        PERMISSIONS.VIEW_FINANCIALS,
        PERMISSIONS.MANAGE_FINANCIALS,
        PERMISSIONS.MANAGE_EXPENSES,
        PERMISSIONS.APPROVE_TIMESHEETS,
        PERMISSIONS.VIEW_REPORTS,
        PERMISSIONS.MANAGE_MARKETING,
        PERMISSIONS.VIEW_SENSITIVE_INFO,
        PERMISSIONS.MANAGE_ROUTES,   // Managers can optimize and manage routes
        PERMISSIONS.VIEW_ROUTES,
        PERMISSIONS.MANAGE_SCHEDULE, // Managers can run weekly territory planner
    ],

    [ROLES.OFFICE]: [
        PERMISSIONS.VIEW_CUSTOMERS,
        PERMISSIONS.MANAGE_CUSTOMERS,
        // s9leads-2a7c5e31 — founder decision 2026-10-05: Office Staff work every request (S9b 9b.4),
        // so they get the Leads page in the sidebar. MANAGE_LEADS gates ONLY that sidebar item.
        PERMISSIONS.MANAGE_LEADS,
        PERMISSIONS.VIEW_JOBS,
        PERMISSIONS.MANAGE_JOBS,
        PERMISSIONS.VIEW_FINANCIALS,
        PERMISSIONS.MANAGE_FINANCIALS,
        PERMISSIONS.MANAGE_EXPENSES,
        PERMISSIONS.COMPLETE_JOBS,
        PERMISSIONS.VIEW_ROUTES,     // Office staff can view routes but not optimize
    ],

    [ROLES.CONSULTANT]: [
        PERMISSIONS.VIEW_CUSTOMERS,
        PERMISSIONS.MANAGE_CUSTOMERS,
        PERMISSIONS.MANAGE_LEADS,
        PERMISSIONS.VIEW_FINANCIALS,
        PERMISSIONS.MANAGE_FINANCIALS,
        PERMISSIONS.MANAGE_MARKETING,
        PERMISSIONS.MANAGE_EXPENSES,
        PERMISSIONS.VIEW_JOBS,
        PERMISSIONS.MANAGE_JOBS,
        PERMISSIONS.VIEW_ROUTES,
    ],

    [ROLES.TECH]: [
        PERMISSIONS.VIEW_CUSTOMERS,
        PERMISSIONS.VIEW_JOBS,
        PERMISSIONS.COMPLETE_JOBS,
        PERMISSIONS.MANAGE_EXPENSES,
        PERMISSIONS.VIEW_ROUTES,     // Techs can view their own optimized route
    ]
};

// officecust-9a4d2c61 — mirror of firestore.rules isHighLevel() (and functions HIGH_LEVEL_ROLES):
// every spelling of a manager-level role that exists in production. Change all three together.
export const HIGH_LEVEL_ROLES = [
    'Admin',
    'Manager',
    'Owner/System Admin',
    'Owner / System Admin',
    'Owner',
    'System Admin',
];

// officecust-9a4d2c61 — who sees EVERY customer, invoice and estimate in pickers and lists
// (founder decision, Marcus 2026-10-04): Office Staff plus every high-level role. Sales Consultants
// and Field Technicians keep their own scope. Replaces the inline ['Admin','Manager',
// 'Owner/System Admin'] lists, which left out Office Staff AND the plain 'Owner' / 'System Admin'
// spellings. Display scoping only — firestore.rules is the real boundary (every staff member may
// READ every customer; invoice/customer WRITES have their own rules).
export const canSeeAllCustomers = (user) =>
    !!user && (user.role === ROLES.OFFICE || HIGH_LEVEL_ROLES.includes(user.role));

// s8portal-4e7b2c95 — S8a portal links + batch invoicing (founder decisions, Marcus 2026-10-04).
// Mirrors of functions/utils/portalKeys.js — the server is the real gate; these only decide what
// the screens show. Change both together.
//   - Copyable portal login link: Owner / Admin / Manager only (whoever holds a link can act as
//     the customer).
//   - Email a portal invite to the address on file (link never shown): anyone who manages
//     estimates — Sales Consultant, Office Staff, high-level. Field Technicians: neither.
//   - Batch create / send invoices: Office Staff + high-level (same set mobile shows).
export const canCreatePortalLink = (user) => !!user && HIGH_LEVEL_ROLES.includes(user.role);

// refundscreen-7c3e5a18 — R1d-1 (BACKLOG row 19, Marcus 2026-10-10): who is OFFERED Refund on an
// invoice — Owner / Admin / Manager, the same six spellings the server's DEPOSIT_RESOLVE_ROLES
// accepts in refundInvoicePayment. Not can(MANAGE_FINANCIALS): that lets Office Staff and Sales
// Consultants through, and the server then answers "permission denied". Display only.
export const canRefundInvoice = (user) => !!user && !!user.role && HIGH_LEVEL_ROLES.includes(user.role);
export const canSendPortalInvite = (user) =>
    !!user && (user.role === ROLES.OFFICE || user.role === ROLES.CONSULTANT || HIGH_LEVEL_ROLES.includes(user.role));
export const canBatchInvoice = (user) =>
    !!user && (user.role === ROLES.OFFICE || HIGH_LEVEL_ROLES.includes(user.role));

// staffpay-6a2d9e47 — H1 follow-up Seam 1 (founder decision, Marcus 2026-10-07): only office roles
// (Owner / Admin / Manager / Office Staff) may record a payment against an invoice — Field
// Technicians and Sales Consultants cannot. Mirror of functions/utils/staffPayment.js
// STAFF_PAYMENT_ROLES (= portalKeys.BATCH_INVOICE_ROLES); the createQuickBooksPayment callable is
// the real gate, this only decides whether the Pay Now button shows. Change both together.
export const canRecordPayment = (user) =>
    !!user && (user.role === ROLES.OFFICE || HIGH_LEVEL_ROLES.includes(user.role));

// s0rolegate-4c8a1f63 — S0 (founder decision, Marcus 2026-10-14): applying a deposit to an invoice
// and sending invoices to QuickBooks are office-only (Owner / Admin / Manager / Office Staff).
// Mirror of the server gates on applyDepositToInvoiceNow, exportInvoiceToQuickBooks and
// batchPushInvoicesToQuickBooks (portalKeys.BATCH_INVOICE_ROLES); the server is the real gate,
// these only decide what the screen offers so a Sales Consultant never meets a refusal.
export const canApplyDepositNow = (user) =>
    !!user && (user.role === ROLES.OFFICE || HIGH_LEVEL_ROLES.includes(user.role));
export const canPushToQuickBooks = (user) =>
    !!user && (user.role === ROLES.OFFICE || HIGH_LEVEL_ROLES.includes(user.role));

// qbrecheck-3a7d9e52 — who is OFFERED "Check again" on an invoice's "QuickBooks has a different
// amount" banner: Owner / Admin / Manager / Office Staff. Mirror of the server's gate in
// recheckQuickBooksInvoiceTotal (portalKeys.BATCH_INVOICE_ROLES), which is the real one.
export const canRecheckQuickBooksAmount = (user) =>
    !!user && (user.role === ROLES.OFFICE || HIGH_LEVEL_ROLES.includes(user.role));

// s9selfapprove-8d1f4b26 — S9b (founder decision 2026-10-05, same principle as S9a's
// self-correction rule): only an Owner or Admin may sign off THEIR OWN time. A Manager's own
// timesheet is approved by an Owner or Admin. Mirrors functions/utils/s9Guards
// SELF_TIME_CORRECTOR_ROLES and the firestore.rules timesheets self-approval check.
export const SELF_TIME_APPROVER_ROLES = ['Admin', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];
export const mustAskOwnerForOwnTime = (user, targetUserId) =>
    !!user && !!targetUserId && targetUserId === user.id && !SELF_TIME_APPROVER_ROLES.includes(user.role);

export const can = (user, permission) => {
    if (!user || !user.role) return false;
    if (user.role === ROLES.OWNER || user.role === ROLES.ADMIN) return true;
    const allowedPermissions = ROLE_PERMISSIONS[user.role] || [];
    return allowedPermissions.includes(permission);
};

// A flag-on Field Technician may EDIT a Job assigned to them while it's still
// open (Pending/Scheduled/In Progress) — never Completed/Canceled. Mirrors the
// firestore.rules techJobUpdatable guard (assigned via assignedTo OR route group;
// active status; Job type) plus the org opt-in flag. Reused by JobFormModal
// (canFillJobForm) and the QuickView edit button so the two never drift.
export const canTechEditJob = (user, companySettings, job) => {
    if (!user || !job) return false;
    if (companySettings?.techJobCreationEnabled !== true) return false;
    if (job.eventType !== 'Job') return false;
    if (!['Pending', 'Scheduled', 'In Progress'].includes(job.status)) return false;
    const uid = user.id;
    return (job.assignedTo || []).includes(uid) || (job.assignedGroupMemberIds || []).includes(uid);
};

// A flag-on Field Technician may create AND edit ANY customer in the org (no
// per-record scoping — matches Jobber's org-wide client permission). Mirrors the
// firestore.rules techCanManageCustomers() helper: role-checked so this grants
// ONLY techs, plus the org opt-in flag. Every other role reaches customer
// management through can(MANAGE_CUSTOMERS); this helper is solely the tech path,
// so callers compose it as `<existing manager gate> || canTechManageCustomers(...)`.
export const canTechManageCustomers = (user, companySettings) => {
    if (!user || !user.role) return false;
    if (companySettings?.techCustomerManagementEnabled !== true) return false;
    return user.role === ROLES.TECH;
};

// jobtimefix-4a9e2d73-b — WHO MAY CORRECT CLOCKED JOB TIME.
//
// These are the SAME SIX STRINGS as functions/index.js JOB_TIME_APPROVER_ROLES and
// firestore.rules isHighLevel(). Keep all three in step; they are the same decision
// expressed in three runtimes.
//
// Deliberately NOT can(user, PERMISSIONS.APPROVE_TIMESHEETS): that helper short-circuits
// only on the literal 'Owner/System Admin' and 'Admin', so it silently refuses a real
// Owner whose role string is the bare 'Owner' that both rules and the callable accept.
// That is a live defect on its own row in BACKLOG.md; this helper sidesteps it rather
// than inheriting it.
//
// Office Staff and Sales Consultant are deliberately OUT even though they hold
// VIEW_FINANCIALS and see the Tracked Labor block: the permission matrix already says
// they schedule and complete jobs but do not sign off time. They get the control
// disabled with a reason, not hidden — they are the roles who notice a bad row and need
// to know whom to escalate to. A Field Technician gets no control at all; a tech editing
// their own time with no approval step is the falsification risk the audit trail exists
// to prevent.
//
// THIS IS A CONVENIENCE GATE, NOT THE SECURITY BOUNDARY. The real gate is
// requireApproveJobTime inside the correctJobPunch callable, which re-reads the caller's
// user doc server-side. Never treat a true here as authorisation.
export const JOB_TIME_APPROVER_ROLES = ['Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];

export const canCorrectJobTime = (user) => !!user?.role && JOB_TIME_APPROVER_ROLES.includes(user.role);

// tsmgr-5a2c8e71 — WHO MAY OPEN WEB TIMESHEETS. Since the initial commit the nav item and route
// asked only for COMPLETE_JOBS, which Managers do not hold — so web hid Timesheets from the very
// role its own screen treats as an approver (mobile has always shown it to them). APPROVE_TIMESHEETS
// was granted to Managers but never read anywhere. Field workers (COMPLETE_JOBS) keep their own
// timesheet; the six approver strings (Managers, and the bare 'Owner'/'System Admin' strings can()
// refuses) now get in too. Nobody loses access. One helper for BOTH the Sidebar item and the route
// in CrmApp, so the two can't drift.
export const canOpenTimesheets = (user) => can(user, PERMISSIONS.COMPLETE_JOBS) || canCorrectJobTime(user);

// tsmgr-5a2c8e71 — WHO SEES MONEY ON WEB TIMESHEETS: rates, gross pay, the Payroll Summary tab,
// its CSV and "Mark as paid". Founder decision 2026-10-02: Managers approve and correct hours but do
// not see pay there — so this is the approver list WITHOUT Manager.
// 🛑 THIS IS A DISPLAY CHOICE, NOT A SECURITY BOUNDARY. firestore.rules deliberately lets same-org
// Managers read users/{id}/private/comp (mailtok-2c7f9e51 allowlist) and payrollRuns/payrollDrafts
// (isHighLevel), and other Manager screens (HR / Team) still read comp. Hiding pay here means the
// Timesheets screen neither shows it nor fetches it for a Manager — nothing more.
export const PAYROLL_VIEWER_ROLES = JOB_TIME_APPROVER_ROLES.filter((r) => r !== ROLES.MANAGER);

export const canViewPayroll = (user) => !!user?.role && PAYROLL_VIEWER_ROLES.includes(user.role);

// wpauthor-5d3f8a26 — WORK PERFORMED ATTRIBUTION. Who may edit or remove SOMEONE
// ELSE'S Work Performed entry. Anyone may always edit their own.
//
// This is the EXACT client mirror of isOffice() in firestore.rules —
// 'Office Staff' plus the six isHighLevel() strings, which is JOB_TIME_APPROVER_ROLES.
// Office Staff are in ON PURPOSE: they are the people who tidy a tech's wording
// before the invoice goes out, and rules test JW4 shows they can already edit the
// flattened Work Performed field today. Leaving them out would have been a
// narrowing dressed up as a security improvement.
//
// WHY NOT can(user, PERMISSIONS.MANAGE_JOBS): can() short-circuits on only the
// literal 'Owner/System Admin' and 'Admin', so it silently refuses a bare
// 'Owner'. An explicit list is the convention canCorrectJobTime already sets for
// exactly that reason.
//
// THIS IS A CONVENIENCE GATE, NOT THE SECURITY BOUNDARY. The real gate is the
// workPerformedEntries block in firestore.rules (isEntryAuthor() || isOffice()),
// proven by rules tests WPA4-WPA7. Never treat a true here as authorisation.
export const WORK_PERFORMED_EDITOR_ROLES = [ROLES.OFFICE, ...JOB_TIME_APPROVER_ROLES];

export const canEditAnyWorkPerformedEntry = (user) =>
    !!user?.role && WORK_PERFORMED_EDITOR_ROLES.includes(user.role);